import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence, useMotionValue, useSpring, useReducedMotion } from "framer-motion";
import { Minus, Plus, ArrowLeft } from "lucide-react";
import { api } from "../lib/api";
import { useShop } from "../context/ShopContext";
import { money } from "../lib/data";

const EASE = [0.76, 0, 0.24, 1];

export default function ProductDetail() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [view, setView] = useState(0);
  const [qty, setQty] = useState(1);
  const [variant, setVariant] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const { addToCart } = useShop();
  const reduce = useReducedMotion();

  const galleryRef = useRef(null);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(my, { stiffness: 55, damping: 14 });
  const rotateY = useSpring(mx, { stiffness: 55, damping: 14 });
  const onTilt = (e) => {
    if (reduce) return;
    const r = galleryRef.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 6);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 5);
  };

  useEffect(() => {
    setProduct(null);
    setView(0);
    setVariant(null);
    api.get("/products").then((r) => {
      const found = r.data.find((p) => p.slug === slug);
      if (found) {
        setProduct(found);
        if (found.variants?.length) {
          setVariant(found.variants[0]);
          const idx = found.images.indexOf(found.variants[0].image);
          setView(idx >= 0 ? idx : 0);
        }
      } else setNotFound(true);
    }).catch(() => setNotFound(true));
  }, [slug]);

  const pickVariant = (v) => {
    setVariant(v);
    const idx = product.images.indexOf(v.image);
    if (idx >= 0) setView(idx);
  };

  if (notFound) {
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center bg-ll-black pt-24 text-ll-bone">
        <h1 className="font-display text-5xl">NOT ON THE SHELF</h1>
        <Link to="/shop" data-testid="notfound-back" className="mt-6 font-tech text-xs tracking-[0.2em] text-ll-red">BACK TO THE SHOP</Link>
      </div>
    );
  }
  if (!product) {
    return <div data-testid="product-loading" className="min-h-screen bg-ll-black pt-40 text-center font-tech text-sm text-ll-bone/50">Loading…</div>;
  }

  const frame = product.wide ? "aspect-[4/3]" : "aspect-[4/5]";

  return (
    <div data-testid="product-page" className="bg-ll-black text-ll-bone">
      <div className="mx-auto max-w-7xl px-5 pb-32 pt-32 md:px-8 md:pb-24 md:pt-40">
        <Link to="/shop" data-testid="product-back-link"
              className="inline-flex items-center gap-2 font-tech text-[11px] tracking-[0.25em] text-ll-bone/60 transition-colors duration-200 hover:text-ll-red">
          <ArrowLeft className="h-4 w-4" /> THE SHELF
        </Link>

        <div className="mt-8 grid gap-10 md:grid-cols-[1.15fr_1fr]">
          <div ref={galleryRef} onMouseMove={onTilt} style={{ perspective: 1100 }}>
            <motion.div style={{ rotateX, rotateY, transformStyle: "preserve-3d" }} className="relative">
              <div className="spotlight" aria-hidden="true" />
              <motion.div
                className={`clip-card relative ${frame} overflow-hidden bg-ll-coal`}
                initial={reduce ? false : { clipPath: "polygon(0 100%, 100% 100%, 100% calc(100% - 22px), calc(100% - 22px) 100%, 0 100%)" }}
                animate={{ clipPath: "polygon(0 0, 100% 0, 100% calc(100% - 22px), calc(100% - 22px) 100%, 0 100%)" }}
                transition={{ duration: 0.85, delay: 0.15, ease: EASE }}
              >
                <AnimatePresence mode="wait">
                  <motion.img
                    key={view}
                    data-testid="product-gallery-image"
                    src={product.images[view]}
                    alt={`${product.name} — photo ${view + 1}`}
                    className="h-full w-full object-cover"
                    initial={{ opacity: 0, scale: 1.03 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.35 }}
                  />
                </AnimatePresence>
              </motion.div>
            </motion.div>
            {product.images.length > 1 && (
              <div className="mt-3 grid grid-cols-4 gap-3">
                {product.images.map((im, i) => (
                  <button
                    key={im}
                    data-testid={`gallery-thumb-${i}`}
                    onClick={() => setView(i)}
                    aria-label={`Show photo ${i + 1}`}
                    className={`aspect-square overflow-hidden border-2 transition-colors duration-200 ${view === i ? "border-ll-red" : "border-transparent hover:border-ll-bone/40"}`}
                  >
                    <img src={im} alt="" loading="lazy" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="md:sticky md:top-32 md:self-start">
            <div className="overflow-hidden">
              <motion.p
                data-testid="product-category"
                className="font-tech text-[11px] tracking-[0.3em] text-ll-red"
                initial={reduce ? false : { y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
              >
                {product.category.toUpperCase()}
              </motion.p>
            </div>
            <h1 className="mt-2 overflow-hidden pb-[0.14em] -mb-[0.14em]">
              <motion.span
                data-testid="product-name"
                className="block font-display text-4xl leading-[0.95] tracking-wide md:text-6xl"
                initial={reduce ? false : { y: "112%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.7, delay: 0.25, ease: EASE }}
              >
                {product.name}
              </motion.span>
            </h1>
            <motion.div
              initial={reduce ? false : { y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              <p data-testid="product-price" className="mt-3 font-tech text-2xl">{money(product.price)}</p>
              <p className="mt-4 max-w-md text-sm leading-relaxed text-ll-bone/60">{product.description}</p>

              {product.variants?.length > 0 && (
                <div className="mt-6" data-testid="variant-picker">
                  <p className="font-tech text-[11px] tracking-[0.25em] text-ll-bone/60">
                    GLOW — <span data-testid="variant-selected" className="text-ll-bone">{variant?.name?.toUpperCase()}</span>
                  </p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    {product.variants.map((v) => (
                      <button
                        key={v.name}
                        data-testid={`variant-${v.name.toLowerCase().replace(/\s+/g, "-")}`}
                        onClick={() => pickVariant(v)}
                        aria-pressed={variant?.name === v.name}
                        className={`flex items-center gap-2 border px-4 py-2.5 font-tech text-[11px] tracking-[0.15em] transition-colors duration-200 ${
                          variant?.name === v.name
                            ? "border-ll-bone bg-ll-bone/10 text-ll-bone"
                            : "border-ll-bone/25 text-ll-bone/60 hover:border-ll-bone/60 hover:text-ll-bone"
                        }`}
                      >
                        <span
                          aria-hidden="true"
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: v.hex, boxShadow: `0 0 8px ${v.hex}, 0 0 16px ${v.hex}66` }}
                        />
                        {v.name.toUpperCase()}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <ul className="mt-6 space-y-2">
                {product.details.map((d) => (
                  <li key={d} className="flex items-center gap-3 font-tech text-xs tracking-[0.1em] text-ll-bone/70">
                    <span className="h-px w-4 bg-ll-red" aria-hidden="true" />{d}
                  </li>
                ))}
              </ul>

              <div className="mt-8 flex items-center gap-4">
                <p className="font-tech text-[11px] tracking-[0.25em] text-ll-bone/60">QTY</p>
                <div className="flex items-center border border-ll-bone/25">
                  <button data-testid="qty-minus" onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2" aria-label="Decrease quantity">
                    <Minus className="h-4 w-4" />
                  </button>
                  <span data-testid="qty-value" className="w-10 text-center font-tech text-sm">{qty}</span>
                  <button data-testid="qty-plus" onClick={() => setQty((q) => Math.min(20, q + 1))} className="px-3 py-2" aria-label="Increase quantity">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>

              <motion.button
                data-testid="add-to-cart-button"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => addToCart(product, qty, variant?.name)}
                className="mt-6 hidden w-full bg-ll-red py-5 font-tech text-sm tracking-[0.25em] text-ll-bone transition-colors duration-200 hover:bg-ll-redhover md:block"
              >
                ADD TO CART — {money(product.price * qty)}
              </motion.button>
              <p className="mt-3 font-tech text-[11px] tracking-[0.1em] text-ll-bone/50">
                SHIPPING CALCULATED AT CHECKOUT — CONFIRMATION + TRACKING BY EMAIL
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      <div data-testid="mobile-purchase-bar" className="fixed inset-x-0 bottom-0 z-50 flex items-center gap-3 border-t border-ll-line bg-ll-black/95 px-4 py-3 backdrop-blur-md md:hidden">
        <div className="flex-1">
          <p className="font-tech text-sm">{money(product.price * qty)}</p>
          <p className="font-tech text-[10px] tracking-[0.15em] text-ll-bone/50">
            {product.name}{variant ? ` — ${variant.name}` : ""}
          </p>
        </div>
        <motion.button data-testid="mobile-add-button" whileTap={{ scale: 0.97 }}
                       onClick={() => addToCart(product, qty, variant?.name)}
                       className="bg-ll-red px-6 py-3 font-tech text-xs tracking-[0.2em] text-ll-bone">
          ADD TO CART
        </motion.button>
      </div>
    </div>
  );
}
